
import React from 'react';
import { EstateAssets, AprAsset, AprAssetType } from '../../../types';
import { Sprout, Trash2, Plus, Info } from 'lucide-react';

interface Props {
    data: EstateAssets;
    onChange: (assets: EstateAssets) => void;
}

const ASSET_TYPE_OPTIONS: { value: AprAssetType; label: string; rate: 100 | 50; minYears: number; desc: string }[] = [
    {
        value: 'owner_occupied',
        label: 'Owner-occupied agricultural land',
        rate: 100,
        minYears: 2,
        desc: 'The deceased farmed the land themselves, or it was occupied by their spouse or a company they controlled. Minimum 2 years ownership (IHTM24120).'
    },
    {
        value: 'tenancy_post_1995',
        label: 'Let land — tenancy starting on/after 1 Sep 1995',
        rate: 100,
        minYears: 7,
        desc: 'Agricultural land let to a tenant under a tenancy beginning on or after 1 September 1995 (IHTM24060). Minimum 7 years ownership.'
    },
    {
        value: 'tenancy_pre_1995',
        label: 'Let land — tenancy starting before 1 Sep 1995',
        rate: 50,
        minYears: 7,
        desc: 'Agricultural land let to a tenant under a tenancy that began before 1 September 1995 (IHTM24061). Minimum 7 years ownership. Only 50% relief.'
    },
];

const rateFromType = (type: AprAssetType | null): 100 | 50 | null => {
    if (!type) return null;
    return ASSET_TYPE_OPTIONS.find(o => o.value === type)?.rate ?? null;
};

const minYearsFromType = (type: AprAssetType | null): number => {
    if (!type) return 2;
    return ASSET_TYPE_OPTIONS.find(o => o.value === type)?.minYears ?? 2;
};

export const AprStep: React.FC<Props> = ({ data, onChange }) => {
    const items = data.apr_assets || [];

    const updateItem = (index: number, changes: Partial<AprAsset>) => {
        const newItems = [...items];
        newItems[index] = { ...newItems[index], ...changes };
        onChange({ ...data, apr_assets: newItems });
    };

    const addItem = () => {
        const newItem: AprAsset = {
            id: `apr_${Date.now()}`,
            label: '',
            asset_type: null,
            estimated_value: null,
            relief_rate: null,
            years_owned: null,
            qualifies: null,
        };
        onChange({ ...data, apr_assets: [...items, newItem] });
    };

    const removeItem = (index: number) => {
        onChange({ ...data, apr_assets: items.filter((_, i) => i !== index) });
    };

    React.useEffect(() => {
        if (items.length === 0) addItem();
    }, []);

    return (
        <div className="max-w-2xl mx-auto space-y-8">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-ink">Agricultural Property Relief</h2>
                <p className="text-ink3 mt-2">
                    Qualifying agricultural property can receive 50% or 100% relief from Inheritance Tax (IHTM24000).
                </p>
            </div>

            {/* Guidance banner */}
            <div className="bg-paper border border-rule-soft rounded-xl p-4 flex gap-3">
                <Info className="w-5 h-5 text-ink2 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-ink space-y-1">
                    <p className="font-bold">What qualifies for APR?</p>
                    <p>APR applies to <strong>agricultural land and property</strong> in the UK, Channel Islands, Isle of Man, or EEA — including farmhouses, farm buildings, and woodland used with farming. Enter the <strong>agricultural value only</strong> — any additional development or hope value is not eligible.</p>
                    <p className="text-ink2 italic">This tool does not give legal or tax advice. Confirm eligibility with a solicitor or chartered tax adviser.</p>
                </div>
            </div>

            <div className="space-y-4">
                {items.map((item, idx) => {
                    const minYears = minYearsFromType(item.asset_type);
                    const ownsTooFew = item.years_owned !== null && item.years_owned < minYears;
                    const reliefAmount = (
                        item.qualifies === true &&
                        item.years_owned !== null && item.years_owned >= minYears &&
                        item.estimated_value &&
                        item.relief_rate
                    ) ? item.estimated_value * (item.relief_rate / 100) : null;

                    return (
                        <div key={item.id} className="bg-white p-5 rounded-xl border border-rule relative">
                            {items.length > 1 && (
                                <button
                                    onClick={() => removeItem(idx)}
                                    className="absolute top-4 right-4 text-sub hover:text-ink2 transition-colors"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            )}

                            <div className="flex flex-col gap-4">
                                {/* Description */}
                                <div>
                                    <label className="block text-xs font-bold text-ink3 uppercase mb-1">Description</label>
                                    <div className="flex items-center gap-2">
                                        <Sprout className="w-5 h-5 text-ink3" />
                                        <input
                                            type="text"
                                            placeholder='e.g. "Oakfield Farm, 40 acres"'
                                            className="w-full border-b border-rule focus:border-accent focus:outline-none py-1 text-ink font-medium"
                                            value={item.label || ''}
                                            onChange={(e) => updateItem(idx, { label: e.target.value })}
                                        />
                                    </div>
                                </div>

                                {/* Asset Type */}
                                <div>
                                    <label className="block text-xs font-bold text-ink3 uppercase mb-2">How was the land used?</label>
                                    <div className="space-y-2">
                                        {ASSET_TYPE_OPTIONS.map(opt => (
                                            <label
                                                key={opt.value}
                                                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors
                                                    ${item.asset_type === opt.value
                                                        ? 'border-rule bg-paper'
                                                        : 'border-rule bg-white hover:border-rule'}`}
                                            >
                                                <input
                                                    type="radio"
                                                    className="mt-0.5 text-ink2"
                                                    name={`apr_type_${item.id}`}
                                                    checked={item.asset_type === opt.value}
                                                    onChange={() => updateItem(idx, {
                                                        asset_type: opt.value,
                                                        relief_rate: opt.rate
                                                    })}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm font-bold text-ink2">{opt.label}</span>
                                                        <span className={`text-[10px] font-black px-1.5 py-0.5 rounded uppercase ${opt.rate === 100 ? 'bg-paper text-ink2' : 'bg-[#F5E8C4] text-[#7A5A00]'}`}>
                                                            {opt.rate}% relief
                                                        </span>
                                                        <span className="text-[10px] text-ink3 font-medium">
                                                            min {opt.minYears}yr
                                                        </span>
                                                    </div>
                                                    <p className="text-[11px] text-ink3 mt-0.5">{opt.desc}</p>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {item.asset_type && (
                                    <>
                                        {/* Value & Years */}
                                        <div className="flex gap-4 flex-wrap">
                                            <div className="flex-1 min-w-[140px]">
                                                <label className="block text-xs font-bold text-ink3 uppercase mb-1">Agricultural Value (£)</label>
                                                <input
                                                    type="number"
                                                    className="w-full border rounded px-3 py-2 text-sm focus:ring-1 focus:ring-accent focus:outline-none"
                                                    placeholder="0"
                                                    value={item.estimated_value ?? ''}
                                                    onChange={(e) => updateItem(idx, { estimated_value: e.target.value ? Number(e.target.value) : null })}
                                                />
                                                <p className="text-[10px] text-ink3 mt-1">Exclude any development or hope value</p>
                                            </div>
                                            <div className="flex-1 min-w-[140px]">
                                                <label className="block text-xs font-bold text-ink3 uppercase mb-1">Years Owned</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    className={`w-full border rounded px-3 py-2 text-sm focus:ring-1 focus:outline-none ${ownsTooFew ? 'border-rule focus:ring-rule' : 'focus:ring-accent'}`}
                                                    placeholder="e.g. 10"
                                                    value={item.years_owned ?? ''}
                                                    onChange={(e) => updateItem(idx, { years_owned: e.target.value ? Number(e.target.value) : null })}
                                                />
                                                {ownsTooFew && (
                                                    <p className="text-[10px] text-ink2 mt-1">
                                                        APR requires at least {minYears} years ownership for this type (IHTM24120).
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Qualifying question */}
                                        <div>
                                            <label className="block text-xs font-bold text-ink3 uppercase mb-2">
                                                Is this genuine agricultural land or property?
                                            </label>
                                            <div className="flex gap-3">
                                                {[
                                                    { val: true, label: 'Yes' },
                                                    { val: false, label: 'No / Not sure' }
                                                ].map(opt => (
                                                    <label key={String(opt.val)} className={`
                                                        flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer text-sm font-medium transition-colors
                                                        ${item.qualifies === opt.val
                                                            ? opt.val ? 'bg-paper border-rule text-ink' : 'bg-paper border-rule text-ink2'
                                                            : 'bg-white border-rule text-ink3 hover:bg-paper'}
                                                    `}>
                                                        <input
                                                            type="radio"
                                                            className="sr-only"
                                                            name={`apr_qualifies_${item.id}`}
                                                            checked={item.qualifies === opt.val}
                                                            onChange={() => updateItem(idx, { qualifies: opt.val })}
                                                        />
                                                        {opt.label}
                                                    </label>
                                                ))}
                                            </div>
                                            {item.qualifies === false && (
                                                <p className="text-[11px] text-[#7A5A00] mt-2 bg-[#FAF3E0] p-2 rounded border border-[#E8D89A]">
                                                    Only genuine agricultural property qualifies for APR. The full value will be included in the taxable estate.
                                                </p>
                                            )}
                                        </div>

                                        {/* Relief summary */}
                                        {reliefAmount !== null && (
                                            <div className="bg-paper border border-rule rounded-lg p-3 flex justify-between items-center">
                                                <span className="text-sm font-bold text-ink">
                                                    {item.relief_rate}% APR Relief Applied
                                                </span>
                                                <span className="text-sm font-mono font-black text-ink2">
                                                    −{reliefAmount.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })}
                                                </span>
                                            </div>
                                        )}
                                        {item.qualifies === true && ownsTooFew && (
                                            <div className="bg-paper border border-rule rounded-lg p-3">
                                                <p className="text-sm font-bold text-ink2">
                                                    Relief not applied — owned less than {minYears} years.
                                                </p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}

                <button
                    onClick={addItem}
                    className="w-full py-3 border-2 border-dashed border-rule rounded-lg text-ink3 font-bold hover:border-sub hover:text-ink2 transition-all flex items-center justify-center gap-2 text-sm"
                >
                    <Plus className="w-4 h-4" /> Add Another Agricultural Asset
                </button>
            </div>
        </div>
    );
};
